/**
 * WalletDecisionRoom — the four-stage room shell for a single decision.
 *
 * Phase 1: TRUST is fully interactive; UNDERSTAND, CHECK and ACT are
 * locked placeholders showing the promise of Phase 2.
 *
 * TRUST stage responsibilities:
 *   1. Prove proposal provenance (who sent it, verified via KY-I).
 *   2. Identify the adviser (name, role, FAIS reg number).
 *   3. Confirm institution KY-I verification against the Altara Trust
 *      Utility registry.
 *   Consumer acknowledges all three → decision advances to
 *   UNDERSTAND_PENDING and an immutable evidence event is written.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ShieldCheck, Landmark, User, BadgeCheck, ArrowLeft, ArrowRight,
  Lock, CheckCircle2, FileText, Sparkles, ListChecks, Compass, Send,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  fetchDecision, acknowledgeTrust, DECISION_STATUS, statusLabel, zar,
} from "@/lib/wallet-decisions";
import { loadWallet } from "@/lib/wallet-storage";
import NaviFace from "@/components/wallet/NaviFace";
import UnderstandStage from "@/components/wallet/decisions/UnderstandStage";
import CheckStage from "@/components/wallet/decisions/CheckStage";
import ActStage from "@/components/wallet/decisions/ActStage";

const TABS = [
  { key: "TRUST",      status: DECISION_STATUS.TRUST,      label: "Trust",      icon: ShieldCheck },
  { key: "UNDERSTAND", status: DECISION_STATUS.UNDERSTAND, label: "Understand", icon: Compass },
  { key: "CHECK",      status: DECISION_STATUS.CHECK,      label: "Check",      icon: ListChecks },
  { key: "ACT",        status: DECISION_STATUS.ACT,        label: "Act",        icon: Send },
];

function stageIdx(stage) {
  return TABS.findIndex(t => t.key === stage);
}

export default function WalletDecisionRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const wallet = loadWallet() || {};
  const deviceId = wallet.device_id;

  const [decision, setDecision] = useState(null);
  const [tab, setTab] = useState("TRUST");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!deviceId) { nav("/wallet/onboarding", { replace: true }); return; }
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchDecision(id, deviceId);
        if (!cancelled) {
          setDecision(d);
          setTab(d.stage === "COMPLETED" ? "ACT" : d.stage);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Decision not found.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [id, deviceId, nav]);

  if (loading) return <RoomSkeleton />;
  if (error || !decision) return <RoomError error={error} />;

  const currentIdx = decision.stage === "COMPLETED" ? TABS.length : stageIdx(decision.stage);

  // When a stage advances, jump the visible tab forward too.
  function onAdvance(next) {
    setDecision(next);
    const newTab = next.stage === "COMPLETED" ? "ACT" : next.stage;
    setTab(newTab);
  }

  return (
    <WalletShell activeTab="decisions">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="decision-room">
        <Link to="/wallet/decisions" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-800" data-testid="decision-room-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to My Decisions
        </Link>

        <RoomHeader d={decision} />

        <StageTabs d={decision} tab={tab} setTab={setTab} currentIdx={currentIdx} />

        {tab === "TRUST" && (
          <TrustStage
            d={decision}
            deviceId={deviceId}
            onAdvance={onAdvance}
          />
        )}
        {tab === "UNDERSTAND" && (
          decision.stage === "TRUST" ? (
            <LockedStage title="Understand" body="Complete the Trust step first — it unlocks the plain-language breakdown of what this proposal actually means." icon={Compass} />
          ) : (
            <UnderstandStage decision={decision} deviceId={deviceId} onAdvance={onAdvance} />
          )
        )}
        {tab === "CHECK" && (
          (decision.stage === "TRUST" || decision.stage === "UNDERSTAND") ? (
            <LockedStage title="Check" body="Complete Understand first — the Consumer Context Check needs your acknowledgements before it can run." icon={ListChecks} />
          ) : (
            <CheckStage decision={decision} deviceId={deviceId} onAdvance={onAdvance} />
          )
        )}
        {tab === "ACT" && (
          (decision.stage === "TRUST" || decision.stage === "UNDERSTAND" || decision.stage === "CHECK") ? (
            <LockedStage title="Act" body="Complete the Consumer Context Check first — Act records your final decision and issues your Consumer Decision Certificate." icon={Send} />
          ) : (
            <ActStage decision={decision} deviceId={deviceId} onAdvance={onAdvance} />
          )
        )}
      </div>
    </WalletShell>
  );
}

/* ─────────────────────── header ─────────────────────── */
function RoomHeader({ d }) {
  return (
    <header className="rounded-2xl border border-slate-100 bg-white p-5 lg:p-6" data-testid="decision-room-header">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
          <Landmark className="w-6 h-6" style={{ color: NAVY }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            Investment proposal · {d.proposal_version}
          </div>
          <h1 className="font-display text-xl lg:text-2xl font-semibold mt-1" style={{ color: NAVY }}>
            {d.proposal_name}
          </h1>
          <div className="mt-1 text-[12px] text-slate-600 flex items-center gap-1.5 flex-wrap">
            <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#059669" }} />
            Sent by <span className="font-semibold" style={{ color: NAVY }}>{d.institution_name}</span>
            · via <span className="font-semibold" style={{ color: NAVY }}>{d.adviser_name}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Commitment</div>
          <div className="font-mono font-semibold text-[15px]" style={{ color: NAVY }}>{zar(d.financial_commitment_zar)}</div>
          <div className="text-[10.5px] text-slate-500 mt-0.5">over {d.investment_term_months} months</div>
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────── stage tabs ─────────────────────── */
function StageTabs({ d, tab, setTab, currentIdx }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-2 flex items-center gap-1 overflow-x-auto" data-testid="decision-room-tabs">
      {TABS.map((t, i) => {
        const Icon = t.icon;
        const done = i < currentIdx;
        const current = i === currentIdx;
        const locked = !done && !current;
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`decision-tab-${t.key.toLowerCase()}`}
            className="flex-1 min-w-[110px] flex items-center gap-2 rounded-xl px-3 py-2.5 text-[12px] font-semibold transition"
            style={{
              background: active ? NAVY : done ? "#ECFDF5" : current ? `${GOLD}18` : "#F8FAFC",
              color:      active ? "white" : done ? "#065F46" : current ? NAVY : "#94A3B8",
            }}
          >
            <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: active ? "rgba(255,255,255,0.15)" : done ? "#059669" : current ? GOLD : "#E2E8F0" }}>
              {done ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "white" }} /> :
                     locked ? <Lock className="w-3 h-3" style={{ color: "#94A3B8" }} /> :
                              <Icon className="w-3.5 h-3.5" style={{ color: active ? "white" : "white" }} />}
            </span>
            <span className="truncate">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────── TRUST stage ─────────────────────── */
function TrustStage({ d, deviceId, onAdvance }) {
  const done = d.status !== DECISION_STATUS.TRUST;
  const [checks, setChecks] = useState({
    provenance:  done,
    adviser:     done,
    institution: done,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const allTicked = checks.provenance && checks.adviser && checks.institution;

  function toggle(key) {
    if (done) return;
    setChecks(c => ({ ...c, [key]: !c[key] }));
  }

  async function submit() {
    if (!allTicked || busy || done) return;
    setBusy(true); setErr(null);
    try {
      const updated = await acknowledgeTrust(d.decision_id, deviceId);
      onAdvance(updated);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not acknowledge — try again.");
    } finally { setBusy(false); }
  }

  return (
    <section className="space-y-4" data-testid="trust-stage">
      {/* NAVI intro */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4 flex items-start gap-3">
        <NaviFace size="md" />
        <div className="min-w-0">
          <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Before you spend a minute understanding this proposal…</div>
          <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">
            First, let's confirm this proposal is legitimately from a real, verified institution and adviser. If any of these details look wrong, stop here and tell me — I'll help you check.
          </div>
        </div>
      </div>

      {/* Provenance card */}
      <ProvenanceCard
        title="Proposal came from a participating institution"
        subtitle={`Source: ${(d.source || "").replace(/_/g, " ")}`}
        icon={FileText}
        checked={checks.provenance}
        onToggle={() => toggle("provenance")}
        done={done}
        testid="trust-check-provenance"
        rows={[
          { k: "Proposal document",   v: `${d.proposal_document_id} · ${d.proposal_version}` },
          { k: "Delivered to Altara", v: new Date(d.sent_at).toLocaleString() },
          { k: "Source channel",      v: "Participating institution · Altara Trust Rail" },
        ]}
      />

      {/* Adviser card */}
      <ProvenanceCard
        title="Adviser identity"
        subtitle={`${d.adviser_name} · ${d.adviser_role}`}
        icon={User}
        checked={checks.adviser}
        onToggle={() => toggle("adviser")}
        done={done}
        testid="trust-check-adviser"
        rows={[
          { k: "Full name",     v: d.adviser_name },
          { k: "Role",          v: d.adviser_role },
          { k: "FAIS reg no.",  v: d.adviser_reg_number || "—" },
          { k: "Attached to",   v: d.institution_name },
        ]}
      />

      {/* Institution card */}
      <ProvenanceCard
        title="Institution verified via Altara KY-I"
        subtitle={d.institution_kyc_verified ? "Verified · in good standing" : "Not verified"}
        icon={BadgeCheck}
        checked={checks.institution}
        onToggle={() => toggle("institution")}
        done={done}
        testid="trust-check-institution"
        rows={[
          { k: "Institution",     v: d.institution_name },
          { k: "Registration",    v: d.institution_registration || "—" },
          { k: "Verified via",    v: d.institution_verified_via || "Altara Trust Utility" },
          { k: "KY-I status",     v: d.institution_kyc_verified ? "Verified" : "Unverified" },
        ]}
        badge={d.institution_kyc_verified ? { label: "KY-I verified", tone: "green" } : null}
      />

      {/* Action row */}
      {done ? (
        <div className="rounded-2xl border p-4 flex items-center gap-3" style={{ background: "#ECFDF5", borderColor: "#A7F3D0" }} data-testid="trust-completed-banner">
          <CheckCircle2 className="w-5 h-5" style={{ color: "#059669" }} />
          <div className="text-[13px]" style={{ color: "#065F46" }}>
            <div className="font-semibold">Trust step complete</div>
            <div className="text-[12px] mt-0.5">You've moved on to <span className="font-semibold">{statusLabel(d.status)}</span>. An immutable evidence event has been recorded.</div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-100 bg-white p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="trust-action-row">
          <div className="text-[12px] text-slate-600 leading-relaxed">
            When all three checks pass, this decision advances to <span className="font-semibold" style={{ color: NAVY }}>Understand</span>.
          </div>
          <button
            onClick={submit}
            disabled={!allTicked || busy}
            data-testid="trust-acknowledge-btn"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40"
            style={{ background: NAVY }}
          >
            {busy ? "Recording evidence…" : "I acknowledge — advance to Understand"}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {err && <div className="text-[12px]" style={{ color: "#B91C1C" }} data-testid="trust-error">{err}</div>}

      {/* Evidence timeline preview */}
      <EvidenceTimeline events={d.evidence_events || []} />
    </section>
  );
}

function ProvenanceCard({ title, subtitle, icon: Icon, rows, checked, onToggle, done, testid, badge }) {
  return (
    <div
      className="rounded-2xl border bg-white p-4 lg:p-5 transition"
      style={{ borderColor: checked ? "#A7F3D0" : "#E2E8F0", background: checked ? "#F0FDF4" : "white" }}
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
          <Icon className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13.5px] font-semibold" style={{ color: NAVY }}>{title}</div>
            {badge && (
              <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: "#D1FAE5", color: "#065F46" }}>
                {badge.label}
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">{subtitle}</div>

          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {rows.map(r => (
              <div key={r.k} className="flex items-baseline gap-2">
                <dt className="text-[10.5px] uppercase tracking-widest text-slate-400 flex-shrink-0 w-24 truncate">{r.k}</dt>
                <dd className="text-[12px] font-medium truncate" style={{ color: NAVY }}>{r.v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <button
          onClick={onToggle}
          disabled={done}
          data-testid={`${testid}-toggle`}
          aria-checked={checked}
          role="checkbox"
          className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 border-2 transition"
          style={{
            background:  checked ? "#059669" : "white",
            borderColor: checked ? "#059669" : "#CBD5E1",
            opacity:     done ? 0.6 : 1,
            cursor:      done ? "default" : "pointer",
          }}
        >
          {checked && <CheckCircle2 className="w-4 h-4 text-white" />}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────── evidence timeline ─────────────────── */
function EvidenceTimeline({ events }) {
  if (!events?.length) return null;
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4" data-testid="evidence-timeline">
      <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>Evidence trail · immutable</div>
      <ol className="mt-3 space-y-2">
        {events.map(e => (
          <li key={e.event_id} className="flex items-start gap-3">
            <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: GOLD }} />
            <div className="min-w-0">
              <div className="text-[12px] font-semibold" style={{ color: NAVY }}>
                <span className="mr-1.5">{e.stage}</span>
                <span className="text-slate-500 font-normal">· {e.event_type.replace(/_/g, " ").toLowerCase()}</span>
              </div>
              <div className="text-[10.5px] text-slate-400">{new Date(e.created_at).toLocaleString()}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ─────────────────────── locked placeholder ─────────────────── */
function LockedStage({ title, body, icon: Icon }) {
  return (
    <section
      className="rounded-2xl border border-dashed p-8 text-center"
      style={{ borderColor: "#CBD5E1", background: "#F8FAFC" }}
      data-testid={`locked-stage-${title.toLowerCase()}`}
    >
      <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3" style={{ background: "white" }}>
        <Icon className="w-6 h-6" style={{ color: "#94A3B8" }} />
      </div>
      <div className="font-display text-[16px] font-semibold" style={{ color: NAVY }}>{title} · coming in Phase 2</div>
      <div className="text-[12.5px] text-slate-500 mt-1.5 leading-relaxed max-w-md mx-auto">{body}</div>
      <div className="mt-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] uppercase tracking-widest font-semibold" style={{ background: "white", color: "#94A3B8" }}>
        <Lock className="w-3 h-3" /> Locked
      </div>
    </section>
  );
}

/* ─────────────────────── skeletons / error ─────────────────── */
function RoomSkeleton() {
  return (
    <WalletShell activeTab="decisions">
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-8 space-y-4" data-testid="decision-room-loading">
        <div className="h-24 bg-white border border-slate-100 rounded-2xl animate-pulse" />
        <div className="h-14 bg-white border border-slate-100 rounded-2xl animate-pulse" />
        <div className="h-40 bg-white border border-slate-100 rounded-2xl animate-pulse" />
      </div>
    </WalletShell>
  );
}
function RoomError({ error }) {
  return (
    <WalletShell activeTab="decisions">
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="decision-room-error">
        <Sparkles className="w-8 h-8 mx-auto text-slate-400" />
        <div className="mt-3 font-display text-lg font-semibold" style={{ color: NAVY }}>
          We couldn't open that decision
        </div>
        <div className="text-[13px] text-slate-500 mt-1">{error}</div>
        <Link to="/wallet/decisions" className="mt-4 inline-flex items-center gap-1 text-[12px] font-medium" style={{ color: NAVY }}>
          ← Back to My Decisions
        </Link>
      </div>
    </WalletShell>
  );
}
